/**
 * RadioCardGroup / RadioCard — a single choice shown as selectable cards
 * (gateway cards, "Gateway network" vs "Custom range", discovered devices).
 *
 * Each card is a native radio inside its label, so the browser supplies the
 * whole keyboard model (Tab into the group, arrows move and select, Space
 * picks) and the selected state is announced. The radio itself is visually
 * replaced by a drawn dot; the card shows focus and the selection border.
 *
 *   <RadioCardGroup name="gw" value={id} onValueChange={setId} aria-label="Gateway">
 *     <RadioCard value="a" title="Bench Pi" description="Online · GatewayPro 0.4" />
 *   </RadioCardGroup>
 */

import { createContext, useContext, useId, type ReactNode } from "react";
import { cn } from "./cn";

interface GroupCtx {
  name: string;
  value: string | null;
  onValueChange: (v: string) => void;
  disabled?: boolean;
}

const Ctx = createContext<GroupCtx | null>(null);

export interface RadioCardGroupProps {
  /** Form name shared by the radios (a generated one when omitted). */
  name?: string;
  value: string | null;
  onValueChange: (value: string) => void;
  disabled?: boolean;
  "aria-label"?: string;
  className?: string;
  children: ReactNode;
}

export function RadioCardGroup({
  name,
  value,
  onValueChange,
  disabled,
  "aria-label": ariaLabel,
  className,
  children,
}: RadioCardGroupProps) {
  const generated = useId();
  return (
    <Ctx.Provider value={{ name: name ?? generated, value, onValueChange, disabled }}>
      <div role="radiogroup" aria-label={ariaLabel} className={cn("flex flex-col gap-2", className)}>
        {children}
      </div>
    </Ctx.Provider>
  );
}

export interface RadioCardProps {
  value: string;
  title: ReactNode;
  description?: ReactNode;
  /** Right-aligned extras (a badge, a status pill). */
  aside?: ReactNode;
  disabled?: boolean;
  className?: string;
  /** Content under the title, shown only while this card is selected. */
  children?: ReactNode;
}

export function RadioCard({ value, title, description, aside, disabled, className, children }: RadioCardProps) {
  const g = useContext(Ctx);
  const id = useId();
  if (!g) throw new Error("RadioCard must be inside <RadioCardGroup>");
  const checked = g.value === value;
  const off = disabled || g.disabled;
  return (
    <div
      className={cn(
        "flex flex-col rounded-lg border transition-colors has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ring",
        checked ? "border-brand bg-brand-muted/30" : "border-border bg-surface",
        off ? "opacity-60" : !checked && "hover:border-on-surface-muted/50",
        className,
      )}
    >
      {/* relative: the visually hidden radio is absolutely positioned; without a
          positioned parent it escapes a scrolling list and stretches the page. */}
      <label className={cn("relative flex items-start gap-3 px-3 py-2.5", off ? "cursor-not-allowed" : "cursor-pointer")}>
        <input
          type="radio"
          name={g.name}
          value={value}
          checked={checked}
          disabled={off}
          onChange={() => g.onValueChange(value)}
          aria-labelledby={`${id}-title`}
          aria-describedby={description ? `${id}-desc` : undefined}
          className="sr-only"
        />
        <span
          aria-hidden
          className={cn(
            "mt-0.5 flex size-4 shrink-0 items-center justify-center rounded-full border",
            checked ? "border-brand" : "border-on-surface-muted/60",
          )}
        >
          {checked && <span className="size-2 rounded-full bg-brand" />}
        </span>
        <span className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span id={`${id}-title`} className="text-sm font-medium text-on-surface">
            {title}
          </span>
          {description && (
            <span id={`${id}-desc`} className="text-xs text-on-surface-muted">
              {description}
            </span>
          )}
        </span>
        {aside && <span className="flex shrink-0 items-center gap-1.5">{aside}</span>}
      </label>
      {checked && children && <div className="px-3 pb-3 pl-10">{children}</div>}
    </div>
  );
}
